import { useEffect, useMemo, useState } from "react";
import SearchableSelect from "./SearchableSelect";
import { getCiudades, getPaises, getRegiones } from "../../../services/locacion";
import "./shipping-zone-form-modal.css";

const INITIAL_FORM = {
  codigo: "",
  nombre: "",
  descripcion: "",
  detalles: [],
};

const INITIAL_DRAFT = {
  pais_id: "",
  region_id: "",
  ciudades_ids: [],
  estado: 1,
};

function ShippingZoneFormModal({
  isOpen,
  zonaData,
  onClose,
  onSubmit,
}) {
  const [activeTab, setActiveTab] = useState("config");

  const [form, setForm] = useState(INITIAL_FORM);
  const [draft, setDraft] = useState(INITIAL_DRAFT);

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const [catalogosError, setCatalogosError] = useState("");

  const [paises, setPaises] = useState([]);
  const [regiones, setRegiones] = useState([]);
  const [ciudades, setCiudades] = useState([]);

  const [loadingPaises, setLoadingPaises] = useState(false);
  const [loadingRegiones, setLoadingRegiones] = useState(false);
  const [loadingCiudades, setLoadingCiudades] = useState(false);

  const modalTitle = zonaData ? "Editar zona de envío" : "Nueva zona de envío";
  const modalSubtitle = zonaData
    ? "Actualiza la configuración general y la cobertura geográfica."
    : "Configura la zona y luego define su cobertura geográfica.";

  const zonaEditionId = zonaData?.tienda_zona_envio_id ?? null;

  const mapItem = (item, fallbackKey) => ({
    ...item,
    id: item.id ?? item[`${fallbackKey}_id`],
    nombre: item.nombre ?? item.descripcion ?? "Sin nombre",
  });

  function construirNombreItem(item) {
    const pais = item?.pais_nombre || item?.pais || "";
    const region = item?.region_nombre || item?.region || "";
    const ciudad = item?.ciudad_nombre || item?.ciudad || "";

    if (ciudad) return [pais, region, ciudad].filter(Boolean).join(" / ");
    if (region) return [pais, region].filter(Boolean).join(" / ");
    return pais || item?.nombre || "Ubicación";
  }

  const cargarPaises = async () => {
    try {
      setLoadingPaises(true);
      setCatalogosError("");

      const response = await getPaises();
      const lista = (Array.isArray(response) ? response : []).map((item) =>
        mapItem(item, "pais")
      );

      setPaises(lista);
    } catch (error) {
      console.error("Error cargando países:", error);
      setCatalogosError("No se pudieron cargar los países.");
      setPaises([]);
    } finally {
      setLoadingPaises(false);
    }
  };

  const cargarRegiones = async (paisId) => {
    if (!paisId) {
      setRegiones([]);
      return;
    }

    try {
      setLoadingRegiones(true);
      setCatalogosError("");

      const response = await getRegiones(paisId);
      const lista = (Array.isArray(response) ? response : []).map((item) => ({
        ...mapItem(item, "region"),
        pais_id: item.pais_id ? Number(item.pais_id) : null,
      }));

      setRegiones(lista);
    } catch (error) {
      console.error("Error cargando regiones:", error);
      setCatalogosError("No se pudieron cargar las regiones.");
      setRegiones([]);
    } finally {
      setLoadingRegiones(false);
    }
  };

  const cargarCiudades = async (regionId) => {
    if (!regionId) {
      setCiudades([]);
      return;
    }

    try {
      setLoadingCiudades(true);
      setCatalogosError("");

      const response = await getCiudades(regionId);
      const lista = (Array.isArray(response) ? response : []).map((item) => ({
        ...mapItem(item, "ciudad"),
        pais_id: item.pais_id ? Number(item.pais_id) : null,
        region_id: item.region_id ? Number(item.region_id) : null,
      }));

      setCiudades(lista);
    } catch (error) {
      console.error("Error cargando ciudades:", error);
      setCatalogosError("No se pudieron cargar las ciudades.");
      setCiudades([]);
    } finally {
      setLoadingCiudades(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    if (zonaData) {
      setForm({
        codigo: zonaData.codigo || "",
        nombre: zonaData.nombre || "",
        descripcion: zonaData.descripcion || "",
        detalles: Array.isArray(zonaData.detalles)
          ? zonaData.detalles.map((item) => ({
              tienda_zona_envio_detalle_id: item.tienda_zona_envio_detalle_id,
              pais_id: item.pais_id || "",
              region_id: item.region_id || "",
              ciudad_id: item.ciudad_id || "",
              estado: Number(item.estado ?? 1),
              nombre: construirNombreItem(item),
              tipo_visual: item.ciudad_id ? "ciudad" : "region",
            }))
          : [],
      });
    } else {
      setForm(INITIAL_FORM);
    }

    setActiveTab("config");
    setDraft(INITIAL_DRAFT);
    setErrors({});
    setSaving(false);
    setCatalogosError("");
    setRegiones([]);
    setCiudades([]);

    void cargarPaises();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, zonaEditionId]);

  const setField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
    }));
  };

  const setDraftField = async (field, value) => {
    if (field === "pais_id") {
      setDraft((prev) => ({
        ...prev,
        pais_id: value,
        region_id: "",
        ciudades_ids: [],
      }));

      setRegiones([]);
      setCiudades([]);

      setErrors((prev) => ({
        ...prev,
        cobertura: undefined,
      }));

      if (value) {
        await cargarRegiones(value);
      }

      return;
    }

    if (field === "region_id") {
      setDraft((prev) => ({
        ...prev,
        region_id: value,
        ciudades_ids: [],
      }));

      setCiudades([]);

      setErrors((prev) => ({
        ...prev,
        cobertura: undefined,
      }));

      if (value) {
        await cargarCiudades(value);
      }

      return;
    }

    setDraft((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      cobertura: undefined,
    }));
  };

  const toggleCiudad = (ciudadId) => {
    setDraft((prev) => {
      const exists = prev.ciudades_ids.some((id) => String(id) === String(ciudadId));

      return {
        ...prev,
        ciudades_ids: exists
          ? prev.ciudades_ids.filter((id) => String(id) !== String(ciudadId))
          : [...prev.ciudades_ids, ciudadId],
      };
    });

    setErrors((prev) => ({
      ...prev,
      cobertura: undefined,
    }));
  };

  const resumenPreview = useMemo(() => {
    if (!draft.pais_id || !draft.region_id) {
      return {
        titulo: "Selecciona país y región",
        texto: "Luego podrás guardar una región completa o elegir ciudades específicas.",
      };
    }

    if (draft.ciudades_ids.length > 0) {
      return {
        titulo: `${draft.ciudades_ids.length} ciudad(es) seleccionada(s)`,
        texto: "Se guardarán únicamente las ciudades marcadas.",
      };
    }

    return {
      titulo: "Región completa",
      texto: "Si no eliges ciudades, se guardará la región completa.",
    };
  }, [draft]);

  const existeDetalle = (nuevo) => {
    return form.detalles.some((item) => {
      const mismoPais = Number(item.pais_id) === Number(nuevo.pais_id);
      const mismaRegion = Number(item.region_id) === Number(nuevo.region_id);

      if (!mismoPais || !mismaRegion) return false;

      if (!item.ciudad_id && !nuevo.ciudad_id) return true;
      if (!item.ciudad_id && nuevo.ciudad_id) return true;
      if (item.ciudad_id && !nuevo.ciudad_id) return true;

      if (item.ciudad_id && nuevo.ciudad_id) {
        return Number(item.ciudad_id) === Number(nuevo.ciudad_id);
      }

      return false;
    });
  };

  const agregarCobertura = () => {
    const nextErrors = {};

    if (!draft.pais_id) {
      nextErrors.cobertura = "Debes seleccionar un país.";
    } else if (!draft.region_id) {
      nextErrors.cobertura = "Debes seleccionar una región.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...nextErrors }));
      return;
    }

    const pais = paises.find((p) => String(p.id) === String(draft.pais_id));
    const region = regiones.find((r) => String(r.id) === String(draft.region_id));

    let nuevosDetalles = [];

    if (draft.ciudades_ids.length === 0) {
      nuevosDetalles = [
        {
          pais_id: Number(draft.pais_id),
          region_id: Number(draft.region_id),
          ciudad_id: null,
          estado: Number(draft.estado),
          nombre: [pais?.nombre, region?.nombre].filter(Boolean).join(" / "),
          tipo_visual: "region",
        },
      ];
    } else {
      nuevosDetalles = draft.ciudades_ids.map((ciudadId) => {
        const ciudad = ciudades.find((c) => String(c.id) === String(ciudadId));

        return {
          pais_id: Number(draft.pais_id),
          region_id: Number(draft.region_id),
          ciudad_id: Number(ciudadId),
          estado: Number(draft.estado),
          nombre: [pais?.nombre, region?.nombre, ciudad?.nombre]
            .filter(Boolean)
            .join(" / "),
          tipo_visual: "ciudad",
        };
      });
    }

    const repetidos = nuevosDetalles.some(existeDetalle);

    if (repetidos) {
      setErrors((prev) => ({
        ...prev,
        cobertura:
          "La región completa o alguna de las ciudades seleccionadas ya existe en esta zona.",
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      detalles: [...prev.detalles, ...nuevosDetalles],
    }));

    setDraft(INITIAL_DRAFT);
    setRegiones([]);
    setCiudades([]);

    setErrors((prev) => ({
      ...prev,
      cobertura: undefined,
      detalles: undefined,
    }));
  };

  const eliminarDetalle = (index) => {
    setForm((prev) => ({
      ...prev,
      detalles: prev.detalles.filter((_, i) => i !== index),
    }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!String(form.codigo || "").trim()) {
      nextErrors.codigo = "El código es requerido";
    }

    if (!String(form.nombre || "").trim()) {
      nextErrors.nombre = "El nombre es requerido";
    }

    if (!Array.isArray(form.detalles) || form.detalles.length === 0) {
      nextErrors.detalles = "Debes agregar al menos una cobertura.";
      setActiveTab("coverage");
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNextTab = () => {
    const nextErrors = {};

    if (!String(form.codigo || "").trim()) {
      nextErrors.codigo = "El código es requerido";
    }

    if (!String(form.nombre || "").trim()) {
      nextErrors.nombre = "El nombre es requerido";
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...nextErrors }));
      return;
    }

    setActiveTab("coverage");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setSaving(true);

      await onSubmit({
        codigo: String(form.codigo).trim(),
        nombre: String(form.nombre).trim(),
        descripcion: String(form.descripcion || "").trim(),
        detalles: form.detalles.map((item) => ({
          tienda_zona_envio_detalle_id: item.tienda_zona_envio_detalle_id,
          pais_id: item.pais_id ? Number(item.pais_id) : null,
          region_id: item.region_id ? Number(item.region_id) : null,
          ciudad_id: item.ciudad_id ? Number(item.ciudad_id) : null,
          estado: Number(item.estado ?? 1),
        })),
      });
    } catch (error) {
      const backendErrors = error?.response?.data?.errors;

      if (Array.isArray(backendErrors) && backendErrors.length > 0) {
        const mappedErrors = {};

        backendErrors.forEach((item) => {
          if (item?.field && !mappedErrors[item.field]) {
            mappedErrors[item.field] = item.message || item.msg || "Campo inválido";
          }
        });

        if (Object.keys(mappedErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...mappedErrors }));
        }
      } else {
        setErrors((prev) => ({
          ...prev,
          submit:
            error?.response?.data?.message ||
            error?.message ||
            "No se pudo guardar la zona de envío.",
        }));
      }
    } finally {
      setSaving(false);
    }
  };

  const grupos = {
    region: form.detalles.filter((d) => d.region_id && !d.ciudad_id),
    ciudad: form.detalles.filter((d) => d.ciudad_id),
  };

  const resumen = {
    total: form.detalles.length,
    regiones: grupos.region.length,
    ciudades: grupos.ciudad.length,
  };

  if (!isOpen) return null;

  return (
    <div
      className="tienda-modal__overlay szfm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="szfm__modal" onClick={(e) => e.stopPropagation()}>
        <div className="szfm__header">
          <div className="szfm__header-copy">
            <div className="szfm__badge">Configuración de zona</div>
            <h3>{modalTitle}</h3>
            <p>{modalSubtitle}</p>
          </div>

          <button type="button" className="szfm__close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="szfm__tabs">
          <button
            type="button"
            className={`szfm__tab ${activeTab === "config" ? "is-active" : ""}`}
            onClick={() => setActiveTab("config")}
          >
            Configuración
          </button>

          <button
            type="button"
            className={`szfm__tab ${activeTab === "coverage" ? "is-active" : ""}`}
            onClick={() => setActiveTab("coverage")}
          >
            Cobertura
            <span className="szfm__tab-count">{resumen.total}</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="szfm__form">
          <div className="szfm__content">
            {activeTab === "config" && (
              <div className="szfm__panel">
                <section className="szfm__card">
                  <div className="szfm__section-title">
                    <h4>Información general</h4>
                    <span>Define la información base de la zona de envío.</span>
                  </div>

                  <div className="szfm__grid szfm__grid--2">
                    <div className="szfm__field">
                      <label>Código</label>
                      <input
                        type="text"
                        value={form.codigo}
                        onChange={(e) => setField("codigo", e.target.value)}
                        placeholder="Ej. GT-PETEN"
                      />
                      {errors.codigo && <span className="szfm__error">{errors.codigo}</span>}
                    </div>

                    <div className="szfm__field">
                      <label>Nombre</label>
                      <input
                        type="text"
                        value={form.nombre}
                        onChange={(e) => setField("nombre", e.target.value)}
                        placeholder="Ej. Petén"
                      />
                      {errors.nombre && <span className="szfm__error">{errors.nombre}</span>}
                    </div>
                  </div>

                  <div className="szfm__field">
                    <label>Descripción</label>
                    <textarea
                      rows="4"
                      value={form.descripcion}
                      onChange={(e) => setField("descripcion", e.target.value)}
                      placeholder="Describe el alcance de esta zona."
                    />
                  </div>
                </section>

                <aside className="szfm__summary-card">
                  <div className="szfm__summary-pill">Resumen</div>
                  <h4>Vista rápida</h4>
                  <p>Completa esta información antes de pasar a la cobertura.</p>

                  <div className="szfm__summary-stats">
                    <div className="szfm__stat">
                      <span className="szfm__stat-label">Coberturas</span>
                      <strong>{resumen.total}</strong>
                    </div>
                    <div className="szfm__stat">
                      <span className="szfm__stat-label">Regiones</span>
                      <strong>{resumen.regiones}</strong>
                    </div>
                    <div className="szfm__stat">
                      <span className="szfm__stat-label">Ciudades</span>
                      <strong>{resumen.ciudades}</strong>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="szfm__btn szfm__btn--primary"
                    onClick={handleNextTab}
                  >
                    Continuar a cobertura
                  </button>
                </aside>
              </div>
            )}

            {activeTab === "coverage" && (
              <div className="szfm__coverage-layout">
                <section className="szfm__card">
                  <div className="szfm__section-title">
                    <h4>Agregar cobertura</h4>
                    <span>
                      Selecciona país y región. Si quieres más precisión, marca ciudades específicas.
                    </span>
                  </div>

                  {catalogosError && <span className="szfm__error">{catalogosError}</span>}
                  {errors.cobertura && <span className="szfm__error">{errors.cobertura}</span>}
                  {errors.submit && <span className="szfm__error">{errors.submit}</span>}

                  <div className="szfm__builder">
                    <div className="szfm__grid szfm__grid--2">
                      <div className="szfm__field">
                        <label>País</label>
                        <SearchableSelect
                          value={draft.pais_id}
                          options={paises}
                          onChange={(value) => setDraftField("pais_id", value)}
                          placeholder="Selecciona un país"
                          loading={loadingPaises}
                          disabled={loadingPaises}
                        />
                      </div>

                      <div className="szfm__field">
                        <label>Región / Departamento</label>
                        <SearchableSelect
                          value={draft.region_id}
                          options={regiones}
                          onChange={(value) => setDraftField("region_id", value)}
                          placeholder={
                            !draft.pais_id
                              ? "Primero selecciona un país"
                              : "Selecciona una región"
                          }
                          loading={loadingRegiones}
                          disabled={!draft.pais_id || loadingRegiones}
                        />
                      </div>
                    </div>

                    <div className="szfm__field">
                      <label>Ciudades / Municipios</label>

                      {!draft.region_id ? (
                        <div className="szfm__empty-state">
                          Primero selecciona una región para ver sus ciudades.
                        </div>
                      ) : loadingCiudades ? (
                        <div className="szfm__empty-state">
                          Cargando ciudades...
                        </div>
                      ) : ciudades.length === 0 ? (
                        <div className="szfm__empty-state">
                          No hay ciudades disponibles para esta región.
                        </div>
                      ) : (
                        <div className="szfm__city-list">
                          {ciudades.map((ciudad) => {
                            const checked = draft.ciudades_ids.some(
                              (id) => String(id) === String(ciudad.id)
                            );

                            return (
                              <label
                                key={ciudad.id}
                                className={`szfm__city-option ${checked ? "is-checked" : ""}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleCiudad(ciudad.id)}
                                />
                                <span className="szfm__city-check">
                                  <svg viewBox="0 0 20 20" aria-hidden="true">
                                    <path d="M5 10.5l3.2 3.2L15 7" />
                                  </svg>
                                </span>
                                <span className="szfm__city-name">{ciudad.nombre}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="szfm__grid szfm__grid--2">
                      <div className="szfm__field">
                        <label>Estado</label>
                        <select
                          value={draft.estado}
                          onChange={(e) => setDraftField("estado", e.target.value)}
                        >
                          <option value={1}>Activo</option>
                          <option value={0}>Inactivo</option>
                        </select>
                      </div>
                    </div>

                    <div className="szfm__builder-footer">
                      <div className="szfm__builder-preview">
                        <span className="szfm__builder-preview-label">Modo de guardado</span>
                        <strong>{resumenPreview.titulo}</strong>
                        <small>{resumenPreview.texto}</small>
                      </div>

                      <button
                        type="button"
                        className="szfm__btn szfm__btn--primary"
                        onClick={agregarCobertura}
                      >
                        Agregar cobertura
                      </button>
                    </div>
                  </div>
                </section>

                <aside className="szfm__card szfm__card--sticky">
                  <div className="szfm__section-title">
                    <h4>Coberturas agregadas</h4>
                    <span>Estas ubicaciones ya forman parte de la zona.</span>
                  </div>

                  {errors.detalles && <span className="szfm__error">{errors.detalles}</span>}

                  {resumen.total === 0 ? (
                    <div className="szfm__empty-state">
                      Aún no has agregado coberturas.
                    </div>
                  ) : (
                    <div className="szfm__groups">
                      {grupos.region.length > 0 && (
                        <div className="szfm__group">
                          <div className="szfm__group-head">
                            <h5>Regiones completas</h5>
                            <span>{grupos.region.length}</span>
                          </div>

                          <div className="szfm__list">
                            {grupos.region.map((item) => {
                              const indexGlobal = form.detalles.indexOf(item);

                              return (
                                <div
                                  className="szfm__row"
                                  key={`region-${item.region_id}-${indexGlobal}`}
                                >
                                  <div className="szfm__row-copy">
                                    <strong>{item.nombre}</strong>
                                    <small>
                                      {Number(item.estado) === 1 ? "Activo" : "Inactivo"}
                                    </small>
                                  </div>

                                  <button
                                    type="button"
                                    className="szfm__remove"
                                    onClick={() => eliminarDetalle(indexGlobal)}
                                  >
                                    Eliminar
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {grupos.ciudad.length > 0 && (
                        <div className="szfm__group">
                          <div className="szfm__group-head">
                            <h5>Ciudades específicas</h5>
                            <span>{grupos.ciudad.length}</span>
                          </div>

                          <div className="szfm__list">
                            {grupos.ciudad.map((item) => {
                              const indexGlobal = form.detalles.indexOf(item);

                              return (
                                <div
                                  className="szfm__row"
                                  key={`ciudad-${item.ciudad_id}-${indexGlobal}`}
                                >
                                  <div className="szfm__row-copy">
                                    <strong>{item.nombre}</strong>
                                    <small>
                                      {Number(item.estado) === 1 ? "Activo" : "Inactivo"}
                                    </small>
                                  </div>

                                  <button
                                    type="button"
                                    className="szfm__remove"
                                    onClick={() => eliminarDetalle(indexGlobal)}
                                  >
                                    Eliminar
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </aside>
              </div>
            )}
          </div>

          <div className="szfm__actions">
            <button
              type="button"
              className="szfm__btn szfm__btn--secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancelar
            </button>

            {activeTab === "coverage" && (
              <button
                type="submit"
                className="szfm__btn szfm__btn--primary"
                disabled={saving}
              >
                {saving ? "Guardando..." : zonaData ? "Actualizar zona" : "Crear zona"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default ShippingZoneFormModal;